const DELIVERY_EVENT = 'pawfresh:delivery-schedule-change';
const CART_LINES_EVENT = 'shopify:cart:lines-update';
const DELIVERY_STORAGE_KEY = 'pawfresh:delivery-selection';

const SHOPIFY_ROOT = window.Shopify?.routes?.root || '/';
const CART_JSON_URL = `${SHOPIFY_ROOT}cart.js`;
const CART_UPDATE_URL = `${SHOPIFY_ROOT}cart/update.js`;

function readCachedDeliverySelection() {
  try {
    const storedValue = window.sessionStorage.getItem(
      DELIVERY_STORAGE_KEY
    );

    if (!storedValue) return null;

    const parsedValue = JSON.parse(storedValue);

    return {
      dateIso: String(parsedValue?.dateIso || ''),
      timeSlot: String(parsedValue?.timeSlot || ''),
    };
  } catch (error) {
    console.warn(
      '[PawFresh delivery] Cached delivery selection could not be read.',
      error
    );

    return null;
  }
}

function writeCachedDeliverySelection(dateIso, timeSlot) {
  try {
    if (!dateIso && !timeSlot) {
      window.sessionStorage.removeItem(
        DELIVERY_STORAGE_KEY
      );

      return;
    }

    window.sessionStorage.setItem(
      DELIVERY_STORAGE_KEY,
      JSON.stringify({
        dateIso: String(dateIso || ''),
        timeSlot: String(timeSlot || ''),
      })
    );
  } catch (error) {
    console.warn(
      '[PawFresh delivery] Delivery selection could not be cached.',
      error
    );
  }
}

function clearCachedDeliverySelection() {
  try {
    window.sessionStorage.removeItem(
      DELIVERY_STORAGE_KEY
    );
  } catch (error) {
    console.warn(
      '[PawFresh delivery] Cached delivery selection could not be cleared.',
      error
    );
  }
}

function clearDeliveryAttributes() {
  clearCachedDeliverySelection();

  return fetch(CART_UPDATE_URL, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      attributes: {
        'Delivery Schedule': '',
        'Delivery Date': '',
        'Delivery Date ISO': '',
        'Delivery Time Slot': '',
        'Delivery Timezone': '',
        'Order due date/time': '',
      },
    }),
  });
}

class PawfreshDeliveryScheduler extends HTMLElement {
  constructor() {
    super();

    this.config = null;
    this.validDates = new Map();
    this.timeSlots = [];
    this.blackoutDates = new Set();

    this.calendarMonth = null;
    this.minimumCalendarMonth = null;
    this.maximumCalendarMonth = null;
    this.todayIso = '';

    this.saveController = null;
    this.saveSequence = 0;
    this.isSaving = false;

    this.schedulerObserver = null;
    this.schedulerRefreshTimer = null;
    this.lastConfigText = '';

    this.handleChange =
      this.handleChange.bind(this);

    this.handleClick =
      this.handleClick.bind(this);

    this.handleFormSubmit =
      this.handleFormSubmit.bind(this);

    this.handleCalendarCancel =
      this.handleCalendarCancel.bind(this);

    this.handleCalendarClose =
  this.handleCalendarClose.bind(this);

this.handleCalendarKeydown =
  this.handleCalendarKeydown.bind(this);

this.handleExternalDeliveryChange =
  this.handleExternalDeliveryChange.bind(this);

this.handleCartLinesUpdate =
  this.handleCartLinesUpdate.bind(this);
  }

  connectedCallback() {
    if (
      this.dataset.initialized === 'true'
    ) {
      return;
    }

    this.dataset.initialized = 'true';
    this.config = this.readConfig();

    if (!this.config) return;

    this.captureElements();

    if (!this.hasRequiredElements) return;

    this.prepareConfiguration();
    this.buildValidDates();
    this.buildTimeOptions();
    this.restoreSelection();
    this.setInitialCalendarMonth();
    this.renderCalendar();

    this.addEventListener(
      'change',
      this.handleChange
    );

    this.addEventListener(
      'click',
      this.handleClick
    );

    this.calendarDialog?.addEventListener(
      'cancel',
      this.handleCalendarCancel
    );

    this.calendarDialog?.addEventListener(
      'close',
      this.handleCalendarClose
    );
    this.calendarDialog?.addEventListener(
  'keydown',
  this.handleCalendarKeydown
);

    document.addEventListener(
      'submit',
      this.handleFormSubmit,
      true
    );

    document.addEventListener(
      DELIVERY_EVENT,
      this.handleExternalDeliveryChange
    );
    document.addEventListener(
  CART_LINES_EVENT,
  this.handleCartLinesUpdate
);

    this.startSchedulerObserver();
    this.refreshCheckoutState();
  }

  disconnectedCallback() {
    this.removeEventListener(
      'change',
      this.handleChange
    );

    this.removeEventListener(
      'click',
      this.handleClick
    );

    this.calendarDialog?.removeEventListener(
      'cancel',
      this.handleCalendarCancel
    );

    this.calendarDialog?.removeEventListener(
      'close',
      this.handleCalendarClose
    );
    this.calendarDialog?.removeEventListener(
  'keydown',
  this.handleCalendarKeydown
);

    document.removeEventListener(
      'submit',
      this.handleFormSubmit,
      true
    );

    document.removeEventListener(
      DELIVERY_EVENT,
      this.handleExternalDeliveryChange
    );
    document.removeEventListener(
  CART_LINES_EVENT,
  this.handleCartLinesUpdate
);

    this.saveController?.abort();
    this.schedulerObserver?.disconnect();

    if (this.schedulerRefreshTimer) {
      window.clearTimeout(
        this.schedulerRefreshTimer
      );
    }

    this.schedulerObserver = null;
    this.schedulerRefreshTimer = null;
    this.dataset.initialized = 'false';
  }

  get hasRequiredElements() {
    return (
      this.dateInput instanceof HTMLInputElement &&
      this.dateTrigger instanceof HTMLButtonElement &&
      this.dateLabelElement instanceof HTMLElement &&
      this.timeSelect instanceof HTMLSelectElement &&
      this.calendarDialog instanceof HTMLDialogElement &&
      this.calendarGrid instanceof HTMLElement
    );
  }

  get timezone() {
    return (
      this.config?.timezone ||
      'Asia/Singapore'
    );
  }

  get selectedDateIso() {
    return this.dateInput?.value || '';
  }

  get selectedTimeSlot() {
    return this.timeSelect?.value || '';
  }

  get isComplete() {
    return Boolean(
      this.selectedDateIso &&
        this.selectedTimeSlot &&
        this.validDates.has(
          this.selectedDateIso
        ) &&
        this.timeSlots.includes(
          this.selectedTimeSlot
        )
    );
  }

  get cartRoot() {
    return (
      this.closest(
        'cart-items-component'
      ) ||
      this.closest('.cart-page') ||
      this.parentElement
    );
  }

  get checkoutButtons() {
    const root =
      this.cartRoot || document;

    return Array.from(
      root.querySelectorAll(
        '[data-delivery-checkout], button[name="checkout"]'
      )
    );
  }

  readConfig() {
    const script = this.querySelector(
      '[data-delivery-config]'
    );

    if (!script?.textContent) {
      return null;
    }

    try {
      this.lastConfigText =
        script.textContent;

      return JSON.parse(
        script.textContent
      );
    } catch (error) {
      console.error(
        '[PawFresh delivery] Invalid scheduler configuration.',
        error
      );

      return null;
    }
  }

  captureElements() {
    this.dateInput =
      this.querySelector(
        '[data-delivery-date]'
      );

    this.dateTrigger =
      this.querySelector(
        '[data-delivery-date-trigger]'
      );

    this.dateLabelElement =
      this.querySelector(
        '[data-delivery-date-label]'
      );

    this.timeSelect =
      this.querySelector(
        '[data-delivery-time]'
      );

    this.messageElement =
      this.querySelector(
        '[data-delivery-message]'
      );

    this.availabilityElement =
      this.querySelector(
        '[data-delivery-availability]'
      );

    this.calendarDialog =
      this.querySelector(
        '[data-delivery-calendar]'
      );

    this.calendarGrid =
      this.querySelector(
        '[data-calendar-grid]'
      );

    this.calendarTitle =
      this.querySelector(
        '[data-calendar-title]'
      );

    this.previousMonthButton =
      this.querySelector(
        '[data-calendar-previous]'
      );

    this.nextMonthButton =
      this.querySelector(
        '[data-calendar-next]'
      );
  }

  prepareConfiguration() {
    this.timeSlots = this.parseLines(
      this.config.timeSlotsText
    );

    this.blackoutDates =
      new Set(
        this.parseLines(
          this.config.blackoutDatesText
        ).filter((value) =>
          /^\d{4}-\d{2}-\d{2}$/.test(
            value
          )
        )
      );
  }

  parseLines(value) {
    return String(value || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  handleChange(event) {
    if (
      event.target !==
      this.timeSelect
    ) {
      return;
    }

    this.markInvalid(false);
    this.setMessage('');
    this.persistSelection();
  }

handleClick(event) {
  const target =
    event.target instanceof Element
      ? event.target
      : null;

  if (!target) return;

  /*
   * Prevent clicks inside the nested calendar from reaching
   * Horizon's cart-drawer backdrop listener.
   */
  const calendar =
    target.closest(
      '[data-delivery-calendar]'
    );

  if (calendar) {
    event.stopPropagation();
  }

  if (
    target.closest(
      '[data-delivery-date-trigger]'
    )
  ) {
    this.openCalendar();
    return;
  }

  if (
    target.closest(
      '[data-calendar-previous]'
    )
  ) {
    this.changeCalendarMonth(-1);
    return;
  }

  if (
    target.closest(
      '[data-calendar-next]'
    )
  ) {
    this.changeCalendarMonth(1);
    return;
  }

  if (
    target.closest(
      '[data-calendar-done]'
    )
  ) {
    event.preventDefault();
    this.closeCalendar();
    return;
  }

  const dateButton =
    target.closest(
      '[data-calendar-date]'
    );

  if (
    dateButton instanceof
      HTMLButtonElement &&
    !dateButton.disabled
  ) {
    this.selectCalendarDate(
      dateButton.dataset.calendarDate || ''
    );

    return;
  }

  /*
   * Clicking the dialog backdrop closes only the calendar.
   * stopPropagation above prevents the cart drawer from closing.
   */
  if (
    target === this.calendarDialog
  ) {
    const bounds =
      this.calendarDialog.getBoundingClientRect();

    const clickedInside =
      event.clientX >= bounds.left &&
      event.clientX <= bounds.right &&
      event.clientY >= bounds.top &&
      event.clientY <= bounds.bottom;

    if (!clickedInside) {
      event.preventDefault();
      this.closeCalendar();
    }
  }
}

  handleFormSubmit(event) {
    const form = event.target;

    if (
      !(
        form instanceof
        HTMLFormElement
      ) ||
      form.id !== 'cart-form'
    ) {
      return;
    }

    if (
      !this.cartRoot?.contains(form) &&
      form !==
        document.getElementById(
          'cart-form'
        )
    ) {
      return;
    }

    const submitter =
      event.submitter;

    const isCheckout =
      submitter?.getAttribute?.(
        'name'
      ) === 'checkout' ||
      submitter?.matches?.(
        '[data-delivery-checkout]'
      );

if (!isCheckout) {
  return;
}

if (
  !this.isComplete ||
  this.isSaving
) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.blockCheckout();
    }
  }

handleCalendarCancel(event) {
  event.preventDefault();
  event.stopPropagation();
  this.closeCalendar();
}
handleCalendarKeydown(event) {
  if (event.key !== 'Escape') return;

  /*
   * Stop Horizon's document-level Escape handler from
   * closing the parent cart drawer.
   */
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  this.closeCalendar();
}

  handleCalendarClose() {
    this.dateTrigger?.setAttribute(
      'aria-expanded',
      'false'
    );
  }

  handleExternalDeliveryChange(
    event
  ) {
    if (event.target === this) return;

    const detail =
      event.detail || {};

    const dateIso = String(
      detail.dateIso || ''
    );

    const timeSlot = String(
      detail.timeSlot || ''
    );

    if (
      dateIso &&
      !this.validDates.has(dateIso)
    ) {
      return;
    }

    this.applySelection(
      dateIso,
      timeSlot
    );

    this.refreshCheckoutState();
  }

  buildValidDates() {
    const today =
      this.getTodayInTimezone();

    const earliest =
      this.getEarliestDate(today);

    const bookingWindow =
      Math.max(
        1,
        Number(
          this.config
            .bookingWindowDays
        ) || 90
      );

    const lastBookable =
      this.addDays(
        today,
        bookingWindow
      );

    const dateFormatter =
      new Intl.DateTimeFormat(
        this.config.dateLocale ||
          'en-SG',
        {
          timeZone: 'UTC',
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }
      );

    this.todayIso =
      this.toIsoDate(today);

    this.validDates.clear();

    for (
      let cursor = earliest;
      cursor <= lastBookable;
      cursor = this.addDays(
        cursor,
        1
      )
    ) {
      if (
        !this.isDeliveryDate(
          cursor
        )
      ) {
        continue;
      }

      const iso =
        this.toIsoDate(cursor);

      this.validDates.set(
        iso,
        dateFormatter.format(cursor)
      );
    }

    const dateKeys =
      Array.from(
        this.validDates.keys()
      );

    const firstDateIso =
      dateKeys[0] || '';

    const lastDateIso =
      dateKeys[
        dateKeys.length - 1
      ] || '';

    this.minimumCalendarMonth =
      firstDateIso
        ? this.monthFromIso(
            firstDateIso
          )
        : this.monthStart(today);

    this.maximumCalendarMonth =
      lastDateIso
        ? this.monthFromIso(
            lastDateIso
          )
        : this.monthStart(today);

    const firstAvailable =
      firstDateIso
        ? this.validDates.get(
            firstDateIso
          )
        : '';

    const availability =
      firstAvailable
        ? `Earliest available delivery: ${firstAvailable}. Cut-off is ${this.formatCutoffTime()} (${this.timezone}).`
        : 'No delivery dates are currently available. Please contact us before checking out.';

    if (
      this.availabilityElement
    ) {
      this.availabilityElement.textContent =
        availability;
    }

    if (
      this.dateTrigger instanceof
      HTMLButtonElement
    ) {
      this.dateTrigger.disabled =
        this.validDates.size === 0;
    }
  }

buildTimeOptions() {
  if (
    !(
      this.timeSelect instanceof
      HTMLSelectElement
    )
  ) {
    return;
  }

  const cachedSelection =
    readCachedDeliverySelection();

  const valueToRestore =
    this.timeSelect.value ||
    cachedSelection?.timeSlot ||
    this.config?.selectedTimeSlot ||
    '';

  const existingPlaceholder =
    this.timeSelect.querySelector(
      'option[value=""]'
    );

  const placeholder =
    existingPlaceholder?.cloneNode(true) ||
    new Option(
      'Select a time slot',
      ''
    );

  this.timeSelect.replaceChildren(
    placeholder
  );

  this.timeSlots.forEach((slot) => {
    this.timeSelect.add(
      new Option(slot, slot)
    );
  });

  if (
    valueToRestore &&
    this.timeSlots.includes(
      valueToRestore
    )
  ) {
    this.timeSelect.value =
      valueToRestore;
  } else {
    this.timeSelect.value = '';
  }
}

restoreSelection({
  preferCached = false,
} = {}) {
  const configuredSelection = {
    dateIso: String(
      this.config?.selectedDateIso || ''
    ),
    timeSlot: String(
      this.config?.selectedTimeSlot || ''
    ),
  };

  const cachedSelection =
    readCachedDeliverySelection();

  const candidates = preferCached
    ? [
        cachedSelection,
        configuredSelection,
      ]
    : [
        configuredSelection,
        cachedSelection,
      ];

  let selectedDateIso = '';
  let selectedTimeSlot = '';

  /*
   * First search for a complete valid selection.
   */
  for (const candidate of candidates) {
    const candidateDate = String(
      candidate?.dateIso || ''
    );

    const candidateTime = String(
      candidate?.timeSlot || ''
    );

    if (
      this.validDates.has(candidateDate) &&
      this.timeSlots.includes(candidateTime)
    ) {
      selectedDateIso = candidateDate;
      selectedTimeSlot = candidateTime;
      break;
    }
  }

  /*
   * Fall back to a valid date without a time only when no
   * complete selection exists.
   */
  if (!selectedDateIso) {
    for (const candidate of candidates) {
      const candidateDate = String(
        candidate?.dateIso || ''
      );

      if (
        candidateDate &&
        this.validDates.has(candidateDate)
      ) {
        selectedDateIso = candidateDate;
        break;
      }
    }
  }

  this.applySelection(
    selectedDateIso,
    selectedTimeSlot
  );

  writeCachedDeliverySelection(
    selectedDateIso,
    selectedTimeSlot
  );

  if (this.isComplete) {
    this.dataset.state = 'saved';
    this.markInvalid(false);

    this.setMessage(
      this.dataset.savedMessage ||
        'Delivery date and time saved.'
    );
  } else {
    this.dataset.state = '';

    this.setMessage(
      this.dataset.requiredMessage ||
        'Select a delivery date and time before checkout.'
    );
  }
}

applySelection(
  dateIso,
  timeSlot
) {
  if (
    !(
      this.dateInput instanceof
      HTMLInputElement
    ) ||
    !(
      this.dateTrigger instanceof
      HTMLButtonElement
    ) ||
    !(
      this.dateLabelElement instanceof
      HTMLElement
    ) ||
    !(
      this.timeSelect instanceof
      HTMLSelectElement
    )
  ) {
    return;
  }

  const validDateIso =
    dateIso &&
    this.validDates.has(dateIso)
      ? dateIso
      : '';

  const validTimeSlot =
    validDateIso &&
    timeSlot &&
    this.timeSlots.includes(timeSlot)
      ? timeSlot
      : '';

  this.dateInput.value =
    validDateIso;

  this.dateLabelElement.textContent =
    validDateIso
      ? this.validDates.get(validDateIso)
      : (
          this.config?.datePlaceholder ||
          'Select delivery date'
        );

  /*
   * The date trigger must only be disabled when there are
   * genuinely no valid delivery dates.
   */
  this.dateTrigger.disabled =
    this.validDates.size === 0;

  /*
   * A valid selected date always unlocks the time selector.
   */
  this.timeSelect.disabled =
    !validDateIso;

  if (
    validTimeSlot &&
    this.timeSelect.querySelector(
      `option[value="${CSS.escape(validTimeSlot)}"]`
    )
  ) {
    this.timeSelect.value =
      validTimeSlot;
  } else {
    this.timeSelect.value = '';
  }

  this.renderCalendar();
}

  setInitialCalendarMonth() {
    const selectedMonth =
      this.selectedDateIso
        ? this.monthFromIso(
            this.selectedDateIso
          )
        : null;

    const firstValidDate =
      Array.from(
        this.validDates.keys()
      )[0] || '';

    this.calendarMonth =
      selectedMonth ||
      (
        firstValidDate
          ? this.monthFromIso(
              firstValidDate
            )
          : this
              .minimumCalendarMonth
      );
  }

  openCalendar() {
    if (
      !(
        this.calendarDialog instanceof
        HTMLDialogElement
      ) ||
      this.dateTrigger?.disabled
    ) {
      return;
    }

    if (this.selectedDateIso) {
      this.calendarMonth =
        this.monthFromIso(
          this.selectedDateIso
        );
    } else if (
      !this.calendarMonth
    ) {
      this.setInitialCalendarMonth();
    }

    this.renderCalendar();

    this.dateTrigger.setAttribute(
      'aria-expanded',
      'true'
    );

    if (
      !this.calendarDialog.open
    ) {
      this.calendarDialog.showModal();
    }

    window.requestAnimationFrame(
      () => {
        const selected =
          this.calendarGrid.querySelector(
            '.is-selected'
          );

        const firstAvailable =
          this.calendarGrid.querySelector(
            '[data-calendar-date]:not(:disabled)'
          );

        (
          selected ||
          firstAvailable ||
          this.previousMonthButton
        )?.focus();
      }
    );
  }

  closeCalendar() {
    if (
      this.calendarDialog?.open
    ) {
      this.calendarDialog.close();
    }

    this.dateTrigger?.setAttribute(
      'aria-expanded',
      'false'
    );

    this.dateTrigger?.focus({
      preventScroll: true,
    });
  }

  changeCalendarMonth(offset) {
    if (!this.calendarMonth) return;

    const nextMonth =
      new Date(
        Date.UTC(
          this.calendarMonth
            .getUTCFullYear(),
          this.calendarMonth
            .getUTCMonth() +
            offset,
          1,
          12
        )
      );

    if (
      this.compareMonths(
        nextMonth,
        this.minimumCalendarMonth
      ) < 0
    ) {
      return;
    }

    if (
      this.compareMonths(
        nextMonth,
        this.maximumCalendarMonth
      ) > 0
    ) {
      return;
    }

    this.calendarMonth =
      nextMonth;

    this.renderCalendar();
  }

selectCalendarDate(dateIso) {
  if (
    !dateIso ||
    !this.validDates.has(dateIso)
  ) {
    return;
  }

  this.dateInput.value =
    dateIso;

  this.dateLabelElement.textContent =
    this.validDates.get(dateIso);

  this.timeSelect.disabled =
    false;

  this.calendarMonth =
    this.monthFromIso(dateIso);

  this.markInvalid(false);
  this.setMessage('');
  this.renderCalendar();

  /*
   * Save immediately, but intentionally keep the calendar
   * open so the customer can choose another date.
   */
  this.persistSelection();

  window.requestAnimationFrame(() => {
    this.calendarGrid
      ?.querySelector('.is-selected')
      ?.focus({
        preventScroll: true,
      });
  });
}

  renderCalendar() {
    if (
      !this.calendarGrid ||
      !this.calendarMonth
    ) {
      return;
    }

    const year =
      this.calendarMonth
        .getUTCFullYear();

    const month =
      this.calendarMonth
        .getUTCMonth();

    const firstDay =
      new Date(
        Date.UTC(
          year,
          month,
          1,
          12
        )
      );

    const leadingBlankCount =
      firstDay.getUTCDay();

    const daysInMonth =
      new Date(
        Date.UTC(
          year,
          month + 1,
          0,
          12
        )
      ).getUTCDate();

    const totalCells =
      Math.ceil(
        (
          leadingBlankCount +
          daysInMonth
        ) /
          7
      ) * 7;

    const titleFormatter =
      new Intl.DateTimeFormat(
        this.config.dateLocale ||
          'en-SG',
        {
          timeZone: 'UTC',
          month: 'long',
          year: 'numeric',
        }
      );

    if (this.calendarTitle) {
      this.calendarTitle.textContent =
        titleFormatter.format(
          firstDay
        );
    }

    this.calendarGrid.replaceChildren();

    for (
      let index = 0;
      index < totalCells;
      index += 1
    ) {
      const dayNumber =
        index -
        leadingBlankCount +
        1;

      if (
        dayNumber < 1 ||
        dayNumber > daysInMonth
      ) {
        const blank =
          document.createElement(
            'span'
          );

        blank.className =
          'paw-delivery-calendar__blank';

        blank.setAttribute(
          'aria-hidden',
          'true'
        );

        this.calendarGrid.append(
          blank
        );

        continue;
      }

      const date =
        new Date(
          Date.UTC(
            year,
            month,
            dayNumber,
            12
          )
        );

      const dateIso =
        this.toIsoDate(date);

      const available =
        this.validDates.has(
          dateIso
        );

      const button =
        document.createElement(
          'button'
        );

      button.type = 'button';

      button.className =
        'paw-delivery-calendar__day';

      button.textContent =
        String(dayNumber);

      button.dataset.calendarDate =
        dateIso;

      button.disabled =
        !available;

      button.setAttribute(
        'role',
        'gridcell'
      );

      button.setAttribute(
        'aria-label',
        available
          ? this.validDates.get(
              dateIso
            )
          : `${dayNumber}, unavailable for delivery`
      );

      if (
        dateIso === this.todayIso
      ) {
        button.classList.add(
          'is-today'
        );
      }

      if (
        dateIso ===
        this.selectedDateIso
      ) {
        button.classList.add(
          'is-selected'
        );

        button.setAttribute(
          'aria-selected',
          'true'
        );
      } else {
        button.setAttribute(
          'aria-selected',
          'false'
        );
      }

      this.calendarGrid.append(
        button
      );
    }

    if (
      this.previousMonthButton instanceof
      HTMLButtonElement
    ) {
      this.previousMonthButton.disabled =
        this.compareMonths(
          this.calendarMonth,
          this.minimumCalendarMonth
        ) <= 0;
    }

    if (
      this.nextMonthButton instanceof
      HTMLButtonElement
    ) {
      this.nextMonthButton.disabled =
        this.compareMonths(
          this.calendarMonth,
          this.maximumCalendarMonth
        ) >= 0;
    }
  }
  scheduleRefreshAfterCartMutation() {
  if (this.schedulerRefreshTimer) {
    window.clearTimeout(
      this.schedulerRefreshTimer
    );
  }

  this.schedulerRefreshTimer =
    window.setTimeout(() => {
      this.schedulerRefreshTimer = null;

      if (!this.isConnected) return;

      this.refreshAfterCartMutation();
    }, 0);
}

handleCartLinesUpdate(event) {
  const resultPromise =
    event.promise;

  if (
    !resultPromise ||
    typeof resultPromise.then !==
      'function'
  ) {
    /*
     * Some cart updates may not expose a promise.
     */
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.scheduleRefreshAfterCartMutation();
      });
    });

    return;
  }

  resultPromise
    .then(({
      cart,
      detail,
    } = {}) => {
      const itemCount =
        cart?.totalQuantity ??
        cart?.item_count ??
        detail?.itemCount;

      if (Number(itemCount) === 0) {
        return;
      }

      /*
       * Wait until Horizon has finished morphing the drawer.
       */
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.scheduleRefreshAfterCartMutation();
        });
      });
    })
    .catch(() => {});
}

refreshAfterCartMutation() {
    const previousDialog =
      this.calendarDialog;

    previousDialog?.removeEventListener(
      'cancel',
      this.handleCalendarCancel
    );

    previousDialog?.removeEventListener(
      'keydown',
      this.handleCalendarKeydown
    );

        const updatedConfig =
          this.readConfig();

        if (updatedConfig) {
          this.config = updatedConfig;
        }

        this.captureElements();

        if (!this.hasRequiredElements) {
          return;
        }

        this.calendarDialog.addEventListener(
          'cancel',
          this.handleCalendarCancel
        );

        this.calendarDialog.addEventListener(
          'close',
          this.handleCalendarClose
        );
        this.calendarDialog.addEventListener(
      'keydown',
      this.handleCalendarKeydown
    );

        this.prepareConfiguration();
        this.buildValidDates();
        this.buildTimeOptions();

        this.restoreSelection({
          preferCached: true,
        });

        this.setInitialCalendarMonth();
        this.renderCalendar();
        this.refreshCheckoutState();
      }
      startSchedulerObserver() {
  if (
    this.schedulerObserver ||
    !('MutationObserver' in window)
  ) {
    return;
  }

  this.schedulerObserver =
    new MutationObserver(() => {
      const currentDateInput =
        this.querySelector(
          '[data-delivery-date]'
        );

      const currentDateTrigger =
        this.querySelector(
          '[data-delivery-date-trigger]'
        );

      const currentDateLabel =
        this.querySelector(
          '[data-delivery-date-label]'
        );

      const currentTimeSelect =
        this.querySelector(
          '[data-delivery-time]'
        );

      const currentConfigText =
        this.querySelector(
          '[data-delivery-config]'
        )?.textContent || '';

      const cachedSelection =
        readCachedDeliverySelection();

      const coreElementsChanged =
        currentDateInput !==
          this.dateInput ||
        currentDateTrigger !==
          this.dateTrigger ||
        currentDateLabel !==
          this.dateLabelElement ||
        currentTimeSelect !==
          this.timeSelect;

      const configChanged =
        currentConfigText !==
          this.lastConfigText;

      const expectedOptionCount =
        this.timeSlots.length + 1;

      const timeOptionsWereRemoved =
        currentTimeSelect instanceof
          HTMLSelectElement &&
        this.timeSlots.length > 0 &&
        currentTimeSelect.options.length <
          expectedOptionCount;

      const selectedDateExists =
        Boolean(
          cachedSelection?.dateIso ||
          (
            currentDateInput instanceof
              HTMLInputElement &&
            currentDateInput.value
          )
        );

      const timeWasDisabled =
        currentTimeSelect instanceof
          HTMLSelectElement &&
        selectedDateExists &&
        currentTimeSelect.disabled;

      const timeValueWasLost =
        currentTimeSelect instanceof
          HTMLSelectElement &&
        Boolean(
          cachedSelection?.timeSlot
        ) &&
        currentTimeSelect.value !==
          cachedSelection.timeSlot;

      if (
        !coreElementsChanged &&
        !configChanged &&
        !timeOptionsWereRemoved &&
        !timeWasDisabled &&
        !timeValueWasLost
      ) {
        return;
      }

      this.scheduleRefreshAfterCartMutation();
    });

  this.schedulerObserver.observe(
    this,
    {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: [
        'disabled',
        'value',
      ],
    }
  );
}

      getTodayInTimezone() {
        let formatter;

        try {
          formatter =
            new Intl.DateTimeFormat(
              'en-CA',
              {
                timeZone:
                  this.timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hourCycle: 'h23',
              }
            );
        } catch (error) {
          console.warn(
            '[PawFresh delivery] Invalid timezone; falling back to Asia/Singapore.',
            this.timezone,
            error
          );

          this.config.timezone =
            'Asia/Singapore';

          return this.getTodayInTimezone();
        }

        const parts =
          Object.fromEntries(
            formatter
              .formatToParts(
                new Date()
              )
              .filter(
                (part) =>
                  part.type !==
                  'literal'
              )
              .map((part) => [
                part.type,
                part.value,
              ])
          );

        this.currentZonedHour =
          Number(parts.hour) || 0;

        this.currentZonedMinute =
          Number(parts.minute) || 0;

        return new Date(
          Date.UTC(
            Number(parts.year),
            Number(parts.month) - 1,
            Number(parts.day),
            12
          )
        );
      }

      getEarliestDate(today) {
        const preparationDays =
          Math.max(
            0,
            Number(
              this.config
                .preparationDays
            ) || 0
          );

        const cutoffHour =
          Math.max(
            0,
            Math.min(
              23,
              Number(
                this.config
                  .cutoffHour
              ) || 0
            )
          );

        const cutoffMinute =
          Math.max(
            0,
            Math.min(
              59,
              Number(
                this.config
                  .cutoffMinute
              ) || 0
            )
          );

        const afterCutoff =
          this.currentZonedHour >
            cutoffHour ||
          (
            this.currentZonedHour ===
              cutoffHour &&
            this.currentZonedMinute >=
              cutoffMinute
          );

        if (
          this.config
            .preparationMode ===
          'working'
        ) {
          let cursor = afterCutoff
            ? this.addDays(today, 1)
            : new Date(
                today.getTime()
              );

          let countedDays = 0;

          while (
            countedDays <
            preparationDays
          ) {
            cursor = this.addDays(
              cursor,
              1
            );

            if (
              this.isPreparationWorkingDay(
                cursor
              )
            ) {
              countedDays += 1;
            }
          }

          return cursor;
        }

        return this.addDays(
          today,
          preparationDays +
            (
              afterCutoff
                ? 1
                : 0
            )
        );
      }

      isPreparationWorkingDay(
        date
      ) {
        const day =
          date.getUTCDay();

        const iso =
          this.toIsoDate(date);

        return (
          day !== 0 &&
          day !== 6 &&
          !this.blackoutDates.has(
            iso
          )
        );
      }

      isDeliveryDate(date) {
        const day =
          date.getUTCDay();

        const iso =
          this.toIsoDate(date);

        const allowedWeekdays =
          Array.isArray(
            this.config
              .allowedWeekdays
          )
            ? this.config
                .allowedWeekdays
            : [];

        return (
          Boolean(
            allowedWeekdays[day]
          ) &&
          !this.blackoutDates.has(
            iso
          )
        );
      }

      addDays(date, days) {
        const copy =
          new Date(
            date.getTime()
          );

        copy.setUTCDate(
          copy.getUTCDate() +
            days
        );

        return copy;
      }

      toIsoDate(date) {
        return [
          date.getUTCFullYear(),
          String(
            date.getUTCMonth() + 1
          ).padStart(2, '0'),
          String(
            date.getUTCDate()
          ).padStart(2, '0'),
        ].join('-');
      }

      monthFromIso(iso) {
        const [year, month] =
          String(iso)
            .split('-')
            .map(Number);

        return new Date(
          Date.UTC(
            year,
            month - 1,
            1,
            12
          )
        );
      }

      monthStart(date) {
        return new Date(
          Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth(),
            1,
            12
          )
        );
      }

      compareMonths(
        firstMonth,
        secondMonth
      ) {
        if (
          !firstMonth ||
          !secondMonth
        ) {
          return 0;
        }

        const firstValue =
          firstMonth.getUTCFullYear() *
            12 +
          firstMonth.getUTCMonth();

        const secondValue =
          secondMonth.getUTCFullYear() *
            12 +
          secondMonth.getUTCMonth();

        return (
          firstValue -
          secondValue
        );
      }

      formatCutoffTime() {
        const hour =
          Math.max(
            0,
            Math.min(
              23,
              Number(
                this.config
                  .cutoffHour
              ) || 0
            )
          );

        const minute =
          Math.max(
            0,
            Math.min(
              59,
              Number(
                this.config
                  .cutoffMinute
              ) || 0
            )
          );

        const date =
          new Date(
            Date.UTC(
              2020,
              0,
              1,
              hour,
              minute
            )
          );

        return new Intl.DateTimeFormat(
          'en-SG',
          {
            timeZone: 'UTC',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }
        ).format(date);
      }

      async persistSelection({
        silent = false,
      } = {}) {
        const dateIso =
          this.selectedDateIso;

        const dateLabel =
          this.validDates.get(
            dateIso
          ) || '';

        const timeSlot =
          dateIso
            ? this.selectedTimeSlot
            : '';

        const sequence =
          ++this.saveSequence;

        writeCachedDeliverySelection(
          dateIso,
          timeSlot
        );

        this.saveController?.abort();

        this.saveController =
          new AbortController();

        this.isSaving = true;
        this.dataset.state =
          'saving';

        if (!silent) {
          this.setMessage(
            this.dataset
              .savingMessage ||
              'Saving your delivery selection…'
          );
        }

        this.refreshCheckoutState();

        try {
          const response =
            await fetch(
              this.dataset
                .cartUpdateUrl ||
                '/cart/update.js',
              {
                method: 'POST',
                credentials:
                  'same-origin',
                headers: {
                  'Content-Type':
                    'application/json',
                  Accept:
                    'application/json',
                },
                signal:
                  this.saveController
                    .signal,
                body: JSON.stringify({
                  attributes: {
                    'Delivery Schedule':
                      dateIso &&
                      timeSlot
                        ? 'Scheduled Delivery'
                        : '',
                    'Delivery Date':
                      dateLabel,
                    'Delivery Date ISO':
                      dateIso,
                    'Delivery Time Slot':
                      timeSlot,
                    'Delivery Timezone':
                      this.timezone,
                    'Order due date/time':
                      dateIso &&
                      timeSlot
                        ? `${dateLabel} — ${timeSlot}`
                        : '',
                  },
                }),
              }
            );

          const cart =
            await response.json();

          if (
            !response.ok ||
            cart.status
          ) {
            throw new Error(
              cart.description ||
                cart.message ||
                'Delivery details could not be saved.'
            );
          }

          if (
            sequence !==
            this.saveSequence
          ) {
            return;
          }

          this.isSaving = false;

          writeCachedDeliverySelection(
            dateIso,
            timeSlot
          );

          if (this.isComplete) {
            this.dataset.state =
              'saved';

            this.markInvalid(false);

            this.setMessage(
              this.dataset
                .savedMessage ||
                'Delivery date and time saved.'
            );
          } else {
            this.dataset.state = '';

            this.setMessage(
              this.dataset
                .requiredMessage ||
                'Select a delivery date and time before checkout.'
            );
          }

          this.dispatchEvent(
            new CustomEvent(
              DELIVERY_EVENT,
              {
                bubbles: true,
                composed: true,
                detail: {
                  valid:
                    this.isComplete,
                  dateIso,
                  dateLabel,
                  timeSlot,
                  cart,
                },
              }
            )
          );
        } catch (error) {
          if (
            error?.name ===
            'AbortError'
          ) {
            return;
          }

          console.error(
            '[PawFresh delivery] Could not save delivery details.',
            error
          );

          this.isSaving = false;
          this.dataset.state =
            'error';

          this.markInvalid(true);

          this.setMessage(
            error?.message ||
              'Delivery details could not be saved. Please try again.'
          );
        } finally {
          if (
            sequence ===
            this.saveSequence
          ) {
            this.isSaving = false;
            this.refreshCheckoutState();
          }
        }
      }

      refreshCheckoutState() {
        const locked =
          !this.isComplete ||
          this.isSaving;

        this.checkoutButtons.forEach(
          (button) => {
            if (
              !(
                button instanceof
                HTMLButtonElement
              )
            ) {
              return;
            }

            button.disabled =
              locked;

            button.dataset.deliveryLocked =
              locked
                ? 'true'
                : 'false';

            button.setAttribute(
              'aria-disabled',
              locked
                ? 'true'
                : 'false'
            );
          }
        );
      }

      blockCheckout() {
        this.markInvalid(true);
        this.dataset.state =
          'error';

        const message =
          this.isSaving
            ? (
                this.dataset
                  .savingMessage ||
                'Please wait while your delivery selection is saved.'
              )
            : (
                this.dataset
                  .requiredMessage ||
                'Select a delivery date and time before checkout.'
              );

        this.setMessage(message);

        const field =
          !this.selectedDateIso
            ? this.dateTrigger
            : this.timeSelect;

        field?.focus({
          preventScroll: true,
        });

        const topOffset =
          this.getStickyHeaderOffset();

        const destination =
          this.getBoundingClientRect()
            .top +
          window.scrollY -
          topOffset -
          16;

        window.scrollTo({
          top: Math.max(
            0,
            destination
          ),
          behavior: 'smooth',
        });
      }

      getStickyHeaderOffset() {
        const bodyValue =
          window
            .getComputedStyle(
              document.body
            )
            .getPropertyValue(
              '--header-height'
            );

        const parsed =
          Number.parseFloat(
            bodyValue
          );

        if (
          Number.isFinite(parsed) &&
          parsed > 0
        ) {
          return parsed;
        }

        const header =
          document.querySelector(
            '[class*="clean-header--"], header-component, header'
          );

        return (
          header instanceof
          HTMLElement
            ? header.offsetHeight
            : 0
        );
      }

      markInvalid(invalid) {
        this.dataset.invalid =
          invalid
            ? 'true'
            : 'false';

        this.dateTrigger?.setAttribute(
          'aria-invalid',
          invalid &&
            !this.selectedDateIso
            ? 'true'
            : 'false'
        );

        this.timeSelect?.setAttribute(
          'aria-invalid',
          invalid &&
            !this.selectedTimeSlot
            ? 'true'
            : 'false'
        );
      }

      setMessage(message) {
        if (this.messageElement) {
          this.messageElement.textContent =
            message || '';
        }
      }
    }

if (
  !customElements.get(
    'pawfresh-delivery-scheduler'
  )
) {
  customElements.define(
    'pawfresh-delivery-scheduler',
    PawfreshDeliveryScheduler
  );
}

if (
  !window
    .__pawfreshDeliveryGlobalBound
) {
  window.__pawfreshDeliveryGlobalBound =
    true;

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target instanceof
        Element
          ? event.target
          : null;

      const checkout =
        target?.closest(
          '[data-delivery-checkout], button[name="checkout"]'
        );

      if (!checkout) return;

      const root =
        checkout.closest(
          'cart-items-component, .cart-page'
        ) || document;

      const scheduler =
        root.querySelector(
          'pawfresh-delivery-scheduler'
        );

      if (
        !(
          scheduler instanceof
          PawfreshDeliveryScheduler
        )
      ) {
        return;
      }

      if (
        !scheduler.isComplete ||
        scheduler.isSaving
      ) {
        event.preventDefault();
        event.stopImmediatePropagation();
        scheduler.blockCheckout();
      }
    },
    true
  );

  document.addEventListener(
    CART_LINES_EVENT,
    (event) => {
      const resultPromise =
        event.promise;

      if (
        !resultPromise ||
        typeof resultPromise.then !==
          'function'
      ) {
        return;
      }

      resultPromise
        .then(
          ({
            cart,
            detail,
          } = {}) => {
            const itemCount =
              cart?.totalQuantity ??
              cart?.item_count ??
              detail?.itemCount;

            if (
              Number(itemCount) !== 0
            ) {
              return;
            }

            clearDeliveryAttributes()
              .catch(() => {});
          }
        )
        .catch(() => {});
    }
  );

  const clearEmptyCartOnLoad =
    () => {
      if (
        document.querySelector(
          'pawfresh-delivery-scheduler'
        )
      ) {
        return;
      }

      fetch(CART_JSON_URL, {
        credentials:
          'same-origin',
        headers: {
          Accept:
            'application/json',
        },
      })
        .then((response) =>
          response.ok
            ? response.json()
            : null
        )
        .then((cart) => {
          if (
            Number(
              cart?.item_count
            ) === 0
          ) {
            return clearDeliveryAttributes();
          }

          return null;
        })
        .catch(() => {});
    };

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      clearEmptyCartOnLoad,
      {
        once: true,
      }
    );
  } else {
    clearEmptyCartOnLoad();
  }
}